import React, { useEffect, useMemo } from 'react'
import { cn } from '../../../../lib/utils'
import '../../../../lib/type-fixes'

interface SpectrumQuestionProps {
  question: {
    id: string
    scale_label_min: string
    scale_label_max: string
    spectrum?: string
    question_text?: string
  }
  value: number | undefined
  onChange: (value: number) => void
}

export function SpectrumQuestion({ question, value, onChange }: SpectrumQuestionProps) {
  // Clear hover states when value changes or component unmounts
  useEffect(() => {
    const clearHoverStates = () => {
      const buttons = document.querySelectorAll('[data-spectrum-button]')
      buttons.forEach(button => {
        button.classList.remove('hover:bg-primary/10', 'hover:bg-accent')
        button.blur()
      })
    }
    
    clearHoverStates()
    return clearHoverStates // Also clear on unmount
  }, [value])

  const handleChange = (newValue: number) => {
    onChange(newValue)
  }

  const preferenceText = useMemo(() => {
    if (!value) return null
    
    if (value === 5) {
      if (question.spectrum) {
        const [aspect1, aspect2] = question.spectrum.split(' vs ')
        return `Balanced approach between ${aspect1} and ${aspect2}`
      }
      return 'Balanced approach'
    }
    
    const getPreferenceStrength = (value: number) => {
      const distance = Math.abs(value - 5)
      if (distance <= 1) return 'Slight'
      if (distance === 2) return 'Moderate'
      if (distance === 3) return 'Strong'
      if (distance === 4) return 'Very strong'
      return 'Strong'
    }
    
    const strength = getPreferenceStrength(value)
    const preference = value < 5 ? question.scale_label_min : question.scale_label_max
    
    return `${strength} preference for ${preference}`
  }, [value, question])

   return (
    <div className="w-full space-y-8">
      <div className="relative pt-12">
        {/* Scale labels */}
        <div className="absolute -top-2 left-0 right-0 flex justify-between text-sm font-medium">
          <div className="w-24 sm:w-32 text-left">{question.scale_label_min}</div>
          <div className="w-24 sm:w-32 text-center text-muted-foreground/70">Balanced</div>
          <div className="w-24 sm:w-32 text-right">{question.scale_label_max}</div>
        </div>

        {/* Spectrum scale */}
        <div className="relative">
          <div className="h-16 rounded-lg bg-muted/50">
            {/* Scale markers */}
            <div className="absolute inset-0 flex justify-between items-center px-2 sm:px-4">
              {Array.from({ length: 9 }, (_, i) => (
                <button
                  key={i}
                  data-spectrum-button
                  onClick={() => handleChange(i + 1)}
                  className={cn(
                    "w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full",
                    "flex items-center justify-center",
                    "text-xs sm:text-sm md:text-base",
                    "transition-colors relative",
                    "touch-none select-none",
                    value === i + 1 ? [
                      "bg-primary text-primary-foreground shadow-md",
                      "md:hover:bg-primary/90 md:active:bg-primary/80",
                      "dark:bg-primary dark:text-primary-foreground",
                      "dark:md:hover:bg-primary/90 dark:md:active:bg-primary/80"
                    ] : [
                      "bg-background text-foreground border border-border",
                      "md:hover:bg-primary/20 md:hover:border-primary/50",
                      "md:active:bg-primary/30",
                      "dark:bg-background dark:text-foreground dark:border-border/40",
                      "dark:md:hover:bg-primary/20 dark:md:hover:border-primary/50",
                      "dark:md:active:bg-primary/30"
                    ]
                  )}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preference text */}
        {value && (
          <div className="absolute -bottom-8 left-0 right-0 text-center text-sm text-muted-foreground">
            {preferenceText}
          </div>
        )}
      </div>
    </div>
  )
}